import {
  normalizeMessage,
  normalizeInteractionAggregate,
  normalizePatient,
  type Conversation,
  type ConversationChannel,
  type ConversationMessage,
} from './conversations-model'
import { validTimestampSchema } from './conversations-contracts'

export const INBOX_PAGE_SIZE = 20
export const INBOX_QUERY_SIZE = INBOX_PAGE_SIZE + 1
export const MESSAGE_PAGE_SIZE = 50
export const MESSAGE_QUERY_SIZE = MESSAGE_PAGE_SIZE + 1

export type InboxCursor = { timestamp: number; patientId: string }
export type MessageCursor = { timestamp: number; messageId: string }

export type InboxPage = {
  items: Conversation[]
  hiddenPatientIds: Set<string>
  cursor: InboxCursor | null
  hasOlder: boolean
}

export type MessagePage = {
  items: ConversationMessage[]
  cursor: MessageCursor | null
  hasOlder: boolean
}

function entries(value: unknown): Array<[string, unknown]> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return []
  }
  return Object.entries(value as Record<string, unknown>)
}

export function parseInboxPage(value: unknown): InboxPage {
  const ordered = entries(value)
    .flatMap(([patientId, patient]) => {
      if (
        typeof patient !== 'object' ||
        patient === null ||
        Array.isArray(patient)
      ) {
        return []
      }
      const lastMessage = (patient as Record<string, unknown>).lastMessage
      if (
        typeof lastMessage !== 'object' ||
        lastMessage === null ||
        Array.isArray(lastMessage)
      ) {
        return []
      }
      const timestamp = validTimestampSchema.safeParse(
        (lastMessage as Record<string, unknown>).timestamp
      )
      return timestamp.success
        ? [{ patientId, timestamp: timestamp.data, patient }]
        : []
    })
    .sort(
      (left, right) =>
        left.timestamp - right.timestamp ||
        left.patientId.localeCompare(right.patientId)
    )
  const hasOlder = ordered.length > INBOX_PAGE_SIZE
  const pageRows = hasOlder ? ordered.slice(1) : ordered
  const boundary = pageRows[0]
  const items = pageRows.flatMap(({ patientId, patient }) => {
    const item = normalizePatient(patientId, patient)
    return item && !item.hidden ? [item] : []
  })
  const hiddenPatientIds = new Set(
    pageRows.flatMap(({ patientId, patient }) => {
      const record = patient as Record<string, unknown>
      return record.trash === true || record.archive === true ? [patientId] : []
    })
  )
  return {
    items,
    hiddenPatientIds,
    cursor: boundary
      ? { timestamp: boundary.timestamp, patientId: boundary.patientId }
      : null,
    hasOlder,
  }
}

// RTDB orders `orderByChild` values as numbers, then strings, then objects
// (startAt(1) excludes missing values and booleans), breaking ties by key.
function rtdbOrderRank(value: unknown) {
  return typeof value === 'number' ? 0 : typeof value === 'string' ? 1 : 2
}

function compareKeys(left: string, right: string) {
  return left < right ? -1 : left > right ? 1 : 0
}

function aggregateActivity(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return undefined
  const display = (raw as { display?: unknown }).display
  if (typeof display !== 'object' || display === null) return undefined
  return (display as { latestActivityAtMs?: unknown }).latestActivityAtMs
}

export function parseAggregateInboxPage(value: unknown): InboxPage {
  // Page over the raw window RTDB returned, not over valid rows: a record that
  // fails the contract still holds its place in the query, so it must count
  // toward `hasOlder` and the cursor or older conversations become unreachable.
  const window = entries(value)
    .map(([aggregateId, raw]) => ({
      aggregateId,
      raw,
      order: aggregateActivity(raw),
    }))
    .sort(
      (left, right) =>
        rtdbOrderRank(left.order) - rtdbOrderRank(right.order) ||
        (typeof left.order === 'number' && typeof right.order === 'number'
          ? left.order - right.order
          : 0) ||
        compareKeys(left.aggregateId, right.aggregateId)
    )
  const fullWindow = window.length > INBOX_PAGE_SIZE
  const pageRows = fullWindow ? window.slice(1) : window
  const boundary = pageRows[0]
  const boundaryTimestamp = validTimestampSchema.safeParse(boundary?.order)
  const rows = pageRows.flatMap(({ aggregateId, raw }) => {
    const item = normalizeInteractionAggregate(aggregateId, raw)
    return item ? [item] : []
  })
  return {
    items: rows.filter((item) => !item.hidden),
    hiddenPatientIds: new Set(
      rows.flatMap((item) => (item.hidden ? [item.conversationId] : []))
    ),
    cursor:
      boundary && boundaryTimestamp.success
        ? { timestamp: boundaryTimestamp.data, patientId: boundary.aggregateId }
        : null,
    // Without a numeric boundary there is no cursor to continue from.
    hasOlder: fullWindow && boundaryTimestamp.success,
  }
}

export function mergeInboxPages(
  newest: InboxPage | null,
  historical: InboxPage[]
): Conversation[] {
  const merged = new Map<string, Conversation>()
  for (const page of historical) {
    for (const patientId of page.hiddenPatientIds) merged.delete(patientId)
    for (const item of page.items) merged.set(item.conversationId, item)
  }
  for (const patientId of newest?.hiddenPatientIds ?? [])
    merged.delete(patientId)
  for (const item of newest?.items ?? []) merged.set(item.conversationId, item)
  return [...merged.values()].sort(
    (left, right) =>
      right.timestamp - left.timestamp ||
      right.conversationId.localeCompare(left.conversationId)
  )
}

// One row per conversation from lists read at different times. Activity only
// moves forward, so the copy with the latest activity is the current one.
export function mergeConversations(...lists: Conversation[][]): Conversation[] {
  const merged = new Map<string, Conversation>()
  for (const list of lists) {
    for (const item of list) {
      const known = merged.get(item.conversationId)
      if (!known || item.timestamp > known.timestamp) {
        merged.set(item.conversationId, item)
      }
    }
  }
  return [...merged.values()].sort(
    (left, right) =>
      right.timestamp - left.timestamp ||
      right.conversationId.localeCompare(left.conversationId)
  )
}

export function parseMessagePage(
  value: unknown,
  threadChannel: ConversationChannel
): MessagePage {
  const ordered = entries(value)
    .flatMap(([messageId, message]) => {
      if (
        typeof message !== 'object' ||
        message === null ||
        Array.isArray(message)
      ) {
        return []
      }
      const timestamp = validTimestampSchema.safeParse(
        (message as Record<string, unknown>).timestamp
      )
      return timestamp.success
        ? [{ messageId, timestamp: timestamp.data, message }]
        : []
    })
    .sort(
      (left, right) =>
        left.timestamp - right.timestamp ||
        left.messageId.localeCompare(right.messageId)
    )
  const hasOlder = ordered.length > MESSAGE_PAGE_SIZE
  const pageRows = hasOlder ? ordered.slice(1) : ordered
  const boundary = pageRows[0]
  return {
    items: pageRows.flatMap(({ messageId, message }) => {
      const item = normalizeMessage(messageId, message, threadChannel)
      return item ? [item] : []
    }),
    cursor: boundary
      ? { timestamp: boundary.timestamp, messageId: boundary.messageId }
      : null,
    hasOlder,
  }
}

export function mergeMessagePages(
  newest: MessagePage | null,
  historical: MessagePage[]
): ConversationMessage[] {
  const merged = new Map<string, ConversationMessage>()
  for (const page of historical) {
    for (const item of page.items) merged.set(item.messageId, item)
  }
  for (const item of newest?.items ?? []) merged.set(item.messageId, item)
  return [...merged.values()].sort(
    (left, right) =>
      left.timestamp - right.timestamp ||
      left.messageId.localeCompare(right.messageId)
  )
}
